import * as React from 'react';
import ProducerInterface from '@app/Interface/ProducerInterface';
import ProducerHelper from '@app/Service/ProducerHelper';
import { PageHeaderToolsItem, Select, SelectOption, SelectOptionObject, SelectVariant } from '@patternfly/react-core';
import { CompassIcon } from '@patternfly/react-icons';
import User from '@app/Interface/UserInterface';
import ProducerModel from '@app/Model/ProducerModel';

interface SelectFpuProps {
    currentUser: User;
    onFpuChange: (fpu: ProducerInterface) => void;
}

/**
 * uses standard select component
 * see: see: https://www.patternfly.org/v4/components/select/
 * 
 * @param currentUser
 * @param onFpuChange
 * @returns 
 */
const SelectFpu: React.FC<SelectFpuProps> = ({ currentUser, onFpuChange }) => {

    const initialFpus: ProducerInterface[] = [];

    const [isOpen, setIsOpen] = React.useState(false);

    const [userFpus, setUserFpus] = React.useState(initialFpus);

    const [selected, setSelected] = React.useState("");

    const [isLoading, setIsLoading] = React.useState(true);

    /**
     * Processes the event when an FPU is selected
     * NOTE: clicking outside the type ahead box also triggers onSelect 
     *       if that happens the value of the entire text is passed instead of the FPU producer id
     *       example: selection = "THE RUIZ FAMILY LLC - 17365", that means they didn't actually change the value
     * @param event 
     * @param selection 
     * @param isPlaceholder 
     */
    const onSelect = (event: React.ChangeEvent | React.MouseEvent, selection: string | SelectOptionObject, isPlaceholder?: boolean) => {

        let selectedValue = "";

        // place holder will clear the seleciton=
        if (isPlaceholder) {

            clearSelection();

        } else {

            setIsOpen(false);

            if (typeof selection === "string") {
                selectedValue = selection;
            } else {
                selectedValue = selection.toString();
            }

            const selectedFpuId = Number(selectedValue) ?? 0;

            setSelected(selectedValue);

            // only update if we have a valid fpu id and if it's different from the current fpu id
            if (isNaN(selectedFpuId) === false && selectedFpuId && currentUser.currentFpuId !== selectedFpuId) {

                const selectedFpu = userFpus.find(fpu => fpu.producerId === selectedFpuId) ?? new ProducerModel();

                onFpuChange(selectedFpu);

            }

        }

    }

    /**
     * Clears the selection and closes the drop-down
     */
    const clearSelection = () => {
        setSelected("");
        setIsOpen(false);
    }

    /**
     * 
     * @param fpu          ProducerInterface 
     * @returns 
     */
    const getDisplayValue = (fpu: ProducerInterface): string => {

        // eslint-disable-next-line prefer-const
        let display = fpu.producerId + " - " + fpu.alphaName;

        return display;

    }

    /**
     * Get the producers the user has access to. 
     * Middleware filters the list based on authorization rules
     * TODO: Add caching for getting a list of producers.
     */
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        const getUserProducers = async () => {

            const producers = await ProducerHelper.getProducers(controller);

            let defaultSelection = "";

            if (currentUser.currentFpuId) {
                defaultSelection = getDefaultProducer(producers, currentUser.currentFpuId);
            } else {
                defaultSelection = getDefaultProducer(producers, currentUser.defaultFpuId);
            }

            if (mounted) {
                setUserFpus(producers);

                setSelected(defaultSelection);

                setIsLoading(false);
            }

        }

        /**
         * gets the default producer selection based 
         * on currently logged in users default fpu id.
         * @param producers 
         * @param defaultFpuId
         * 
         * @returns 
         */
        const getDefaultProducer = (producers: ProducerInterface[], defaultFpuId: number): string => {

            let selection = "";

            const producer = producers.find(producer => producer.producerId === defaultFpuId);

            if (producer) {
                selection = getDisplayValue(producer);
            }

            return selection;

        }

        getUserProducers();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [currentUser.currentFpuId, currentUser.defaultFpuId]);

    const titleId = "select-fpu-id";

    /**
     * render the drop-down tool. 
     * NOTE: patternfly breakpoints are based on minimum width. Think opposite
     *       set default width to apply to all break-points. Then set desired 
     *       width for larger breakpoints. In this case. For the drop-down tool. 
     *       set all widths to 33%. Then breakpoints `md` or higher, set width to 100%
     */
    return (

        <PageHeaderToolsItem>
            <span id={titleId} hidden>
                Select an FPU
            </span>
            <Select
                variant={SelectVariant.single}
                placeholderText="Select an FPU"
                aria-label="Select an FPU"
                aria-labelledby={titleId}
                isOpen={isOpen}
                onToggle={() => setIsOpen(!isOpen)}
                onSelect={onSelect}
                selections={selected}
                loadingVariant={isLoading ? "spinner" : undefined}
                maxHeight={400}
                position="right"
            >
                {userFpus.map(fpu => (
                    <SelectOption
                        key={fpu.producerId}
                        value={fpu.producerId}
                        description={fpu.county + ", " + fpu.state}
                    >
                        {getDisplayValue(fpu)}
                    </SelectOption>
                ))}

            </Select>

        </PageHeaderToolsItem>

    );

}

export default SelectFpu;